import {MaybeValue} from '@aboutyou/utils';
import {useCallback, useEffect} from 'react';
import {useActivateAbTestByFeatureFlagCallback} from './abtests/optimizely/useActivateAbTestCallback';
import {useMergedFeatureFlagConfig} from './Provider';
import {
  AvailableFeatureNames,
  AvailableFeatureToggles,
  Experiment,
  ExtractExperiment,
  FeatureToggleConfig,
  InferVariant,
  isFeatureFlagWithExperiment,
  isFeatureFlagWithExperiments,
} from './types';
import {useIsSoftFeatureToggleActive} from './useIsSoftFeatureToggleActive';

interface Config<E> {
  abTest?: E | boolean;
}

export function useIsFeatureActive<
  T extends AvailableFeatureNames,
  F extends AvailableFeatureToggles[T],
  E extends ExtractExperiment<F>,
  N extends MaybeValue<E, 'name'>,
  V extends InferVariant<E, N, F['defaultValue']>,
>(
  featureName: T,
  /**
   * Options:
   * - abTest: if true, activates the ab test on hydration/mount. Defaults to false
   */
  config: Config<N> = {abTest: false},
): {
  /**
   * boolean: true or false if the feature is enabled in any variant excluding control
   */
  isActive: boolean;
  /**
   * string: returns the currently activated variant including control
   */
  variant?: V;
  /**
   * Callback to activate the experiment programmatically. Automatically activates the assigned experiment, but
   * if you need to filter to a specific experiment you can provide the name. Useful if you have multiple experiments
   * running under the same feature flag e.g. mweb/desktop variants
   * @param experimentName
   * @returns void
   */
  activateAbTest: (experimentName?: N) => void;
} {
  const featureFlagConfig = useMergedFeatureFlagConfig();
  const currentConfig = featureFlagConfig[featureName];
  const activate = useActivateAbTestByFeatureFlagCallback(featureName);

  const value = useIsSoftFeatureToggleActive(featureName);

  // A feature is considered active if it's either true, or something other than
  // control is enabled (AB test). All controls end with -c according to conventions
  //
  // Note: for backward compatibility, checks for `c` and `control` are kept. However, should be removed
  // once the feature flags that use these are removed
  const isActive = isValueActive(value);
  const mappingValue =
    typeof value === 'string'
      ? getMappingValue(value, currentConfig)
      : undefined;

  const activateAbTest = useCallback(
    (name?: N | boolean) => {
      if (typeof name === 'string') {
        activate(name);
        return;
      }

      if (currentConfig && isFeatureFlagWithExperiments(currentConfig)) {
        currentConfig.experiment.forEach(({name}) => {
          activate(name);
        });
      } else if (currentConfig && isFeatureFlagWithExperiment(currentConfig)) {
        activate(currentConfig.experiment.name);
      }
    },
    [activate, currentConfig],
  );

  useEffect(() => {
    if (config.abTest) {
      activateAbTest(config.abTest);
    }
  }, [activateAbTest, config.abTest]);

  return {
    isActive,
    variant: mappingValue as V,
    activateAbTest,
  };
}

function getMappingValue(
  value: string,
  config: FeatureToggleConfig,
): string | undefined {
  if (config && isFeatureFlagWithExperiments(config)) {
    const mapping = config.experiment.find(exp =>
      Object.keys(exp.mapping).includes(value),
    )?.mapping;
    if (mapping) {
      return mapping[value as keyof Experiment['mapping']];
    }
  } else if (config && isFeatureFlagWithExperiment(config)) {
    return config.experiment.mapping?.[value as keyof Experiment['mapping']];
  }
}

function isValueActive(v: string | boolean) {
  if (typeof v === 'string') {
    return !v.endsWith('-c') && v !== 'control' && v !== 'c';
  }
  return v;
}
